"use client";

import React, { useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';

import config from '@/config';
import { safeInternalReturnTo } from '@/libs/safe-return-to';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { Provider } from '@supabase/supabase-js';

export const runtime = 'edge';


// This a login/singup page for Supabase Auth.
// Successfull login redirects to /api/auth/callback where the Code Exchange is processed (see app/api/auth/callback/route.js).
export default function Login({ params: { locale } }: { params: { locale: string } }) {
  const supabase = createClientComponentClient();
  const router = useRouter();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [magicLinkEmail, setMagicLinkEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isSignUp, setIsSignUp] = useState<boolean>(false);

  const getReturnTo = () => {
    if (typeof window === 'undefined') return config.auth.callbackUrl;
    const value = new URLSearchParams(window.location.search).get('returnTo');
    return safeInternalReturnTo(value, config.auth.callbackUrl);
  };

  const getCallbackUrl = () => {
    const callback = new URL('/api/auth/callback', window.location.origin);
    callback.searchParams.set('returnTo', getReturnTo());
    callback.searchParams.set('locale', locale);
    return callback.toString();
  };

  const handlePasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }

    setIsLoading(true);

    try {
      const redirectURL = getCallbackUrl();

      if (isSignUp) {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectURL,
          },
        });

        if (error) {
          toast.error(error.message || "Sign up failed");
        } else if (data.session) {
          toast.success("Account created!");
          router.push(`/api/auth/callback?returnTo=${encodeURIComponent(getReturnTo())}&locale=${encodeURIComponent(locale)}`);
        } else {
          toast.success("Account created! Please check your email to verify your account.");
          setPassword("");
        }
      } else {
        // Sign in
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          toast.error(error.message || "Login failed");
        } else {
          toast.success("Login successful!");
          router.push(`/api/auth/callback?returnTo=${encodeURIComponent(getReturnTo())}&locale=${encodeURIComponent(locale)}`);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (
    e: any,
    options: {
      type: string;
      provider?: Provider;
    }
  ) => {
    e?.preventDefault();

    setIsLoading(true);

    try {
      const { type, provider } = options;
      const redirectURL = getCallbackUrl();

      if (type === "oauth") {
        await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: redirectURL,
          },
        });
      } else if (type === "magic_link") {
        if (!magicLinkEmail) {
          toast.error("Please enter your email address");
          return;
        }

        await supabase.auth.signInWithOtp({
          email: magicLinkEmail,
          options: {
            emailRedirectTo: redirectURL,
          },
        });

        toast.success("Check your emails!");
        setIsDisabled(true);
      }

    } catch (error: any) {
      toast.error(error.message || "login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="p-8 md:p-24" data-theme={config.colors.theme}>
      <div className="text-center mb-4">
        <Link href="/" className="btn btn-ghost btn-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M15 10a.75.75 0 01-.75.75H7.612l2.158 1.96a.75.75 0 11-1.04 1.08l-3.5-3.25a.75.75 0 010-1.08l3.5-3.25a.75.75 0 111.04 1.08L7.612 9.25h6.638A.75.75 0 0115 10z"
              clipRule="evenodd"
            />
          </svg>
          Home
        </Link>
      </div>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-center mb-12">
        {isSignUp ? `Sign up to ${config.appName}` : `Sign-in to ${config.appName}`}
      </h1>

      <div className="space-y-8 max-w-xl mx-auto">
        {/* Google OAuth - First Priority */}
        <button
          className="btn btn-block"
          onClick={(e) =>
            handleSignup(e, { type: "oauth", provider: "google" })
          }
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="loading loading-spinner loading-xs"></span>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              viewBox="0 0 48 48"
            >
              <path
                fill="#FFC107"
                d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
              />
              <path
                fill="#FF3D00"
                d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
              />
              <path
                fill="#4CAF50"
                d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
              />
              <path
                fill="#1976D2"
                d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
              />
            </svg>
          )}
          {isSignUp ? "Sign up with Google" : "Sign in with Google"}
        </button>

        <div className="divider text-xs text-base-content/50 font-medium">
          OR
        </div>

        {/* Toggle between Sign In and Sign Up */}
        <div className="flex justify-center mb-4">
          <div className="btn-group">
            <button
              className={`btn ${!isSignUp ? 'btn-active' : ''}`}
              onClick={() => {
                setIsSignUp(false);
                setPassword("");
              }}
            >
              Sign In
            </button>
            <button
              className={`btn ${isSignUp ? 'btn-active' : ''}`}
              onClick={() => {
                setIsSignUp(true);
                setPassword("");
              }}
            >
              Sign Up
            </button>
          </div>
        </div>

        {/* Email and Password Form */}
        <form
          className="form-control w-full space-y-4"
          onSubmit={handlePasswordAuth}
        >
          <input
            required
            type="email"
            value={email}
            autoComplete="email"
            placeholder="Email address"
            className="input input-bordered w-full placeholder:opacity-60"
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />

          <input
            required
            type="password"
            value={password}
            autoComplete={isSignUp ? "new-password" : "current-password"}
            placeholder="Password (min. 6 characters)"
            className="input input-bordered w-full placeholder:opacity-60"
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            minLength={6}
          />

          <button
            className="btn btn-primary btn-block"
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              isSignUp ? "Create Account" : "Sign In"
            )}
          </button>
        </form>

        <div className="divider text-xs text-base-content/50 font-medium">
          OR
        </div>

        {/* Magic Link Form */}
        <form
          className="form-control w-full space-y-4"
          onSubmit={(e) => handleSignup(e, { type: "magic_link" })}
        >
          <input
            required
            type="email"
            value={magicLinkEmail}
            autoComplete="email"
            placeholder="Email address"
            className="input input-bordered w-full placeholder:opacity-60"
            onChange={(e) => setMagicLinkEmail(e.target.value)}
            disabled={isLoading || isDisabled}
          />

          <button
            className="btn btn-outline btn-block"
            disabled={isLoading || isDisabled}
            type="submit"
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              "Send Magic Link"
            )}
          </button>
        </form>
      </div>
    </main>
  );
}
